import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getCurrentUser, onAuthStateChange } from "@/lib/auth";
import { isUserAdmin } from "@/lib/admin";
import {
  calculateOverallProgress,
  calculateJointProgress,
  isOnboardingComplete,
  getOrCreateUserRelationship,
  getRelationship,
  getComfortLevel,
  type Relationship
} from "@/lib/storage";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { trackEvent } from "@/lib/analytics";
import { hasAnonResponses } from "@/lib/local-storage-adapter";
import { migrateLocalResponsesToFirestore } from "@/lib/migrate-local-responses";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AppHeader from "@/components/AppHeader";
import { PaymentModal } from "@/components/PaymentModal";
import { PartnerPairing } from "@/components/PartnerPairing";
import Footer from "@/components/Footer";
import { PRICING } from "@/lib/stripe";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";
import { completePendingEncryption } from "@/lib/version-history";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  GitCompare,
  MessagesSquare,
  FileCheck,
  PenTool,
  Clock,
  Lock,
  HelpCircle,
  User,
  ShoppingBag,
  Sparkles,
  Loader2
} from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [progress, setProgress] = useState({ total: 0, answered: 0, percentage: 0 });
  const [jointProgress, setJointProgress] = useState({ answered: 0, total: 0, percentage: 0 });
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [relationshipId, setRelationshipId] = useState<string | null>(null);
  const [relationship, setRelationship] = useState<Relationship | null>(null);
  const [comfortLevel, setComfortLevel] = useState<number | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationMessage, setMigrationMessage] = useState<string | null>(null);

  const { isPremium, profile: premiumProfile } = usePremiumAccess(user?.id);

  // Scroll to top when page loads
  useScrollToTop();

  // Load progress data
  const loadProgress = async (relId: string, userId: string) => {
    const userProgress = await calculateOverallProgress(relId, userId);
    setProgress(userProgress);
  };

  // Load joint collaboration progress
  const loadJointProgress = async (relId: string) => {
    try {
      const jProgress = await calculateJointProgress(relId);
      setJointProgress(jProgress);
    } catch (error) {
      console.log('[Dashboard] Could not load joint progress (may not have permissions yet):', error);
      // Set default progress if can't load (will use filtered count)
      const fallbackProgress = await calculateJointProgress(relId);
      setJointProgress(fallbackProgress);
    }
  };

  // Load onboarding status
  const loadOnboardingStatus = async (relId: string, userId: string) => {
    const completed = await isOnboardingComplete(relId, userId);
    setOnboardingComplete(completed);
  };

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange(async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (!currentUser) {
        navigate("/login");
      } else {
        // Check admin status first
        const adminStatus = await isUserAdmin(currentUser.id);
        setIsAdmin(adminStatus);

        // Get relationship for this user (returns null if none exists, auto-creates for admins)
        const relId = await getOrCreateUserRelationship(currentUser.id, currentUser.email, adminStatus);
        setRelationshipId(relId);

        if (relId) {
          // Load relationship details
          const rel = await getRelationship(relId);
          setRelationship(rel);

          // Load progress when user is authenticated (with relationshipId)
          await loadProgress(relId, currentUser.id);
          await loadJointProgress(relId);

          // Load comfort level
          const userComfortLevel = await getComfortLevel(relId, currentUser.id);
          setComfortLevel(userComfortLevel);

          // Check onboarding status (with relationshipId)
          isOnboardingComplete(relId, currentUser.id).then((completed) => {
            setOnboardingComplete(completed);
          });

          // FREEMIUM: Auto-migrate any anonymous localStorage responses
          if (hasAnonResponses()) {
            console.log('[Dashboard] Detected anonymous responses — starting migration');
            setMigrating(true);
            try {
              const migration = await migrateLocalResponsesToFirestore(
                currentUser.id, currentUser.email
              );
              if (migration.success && migration.responsesCount > 0) {
                setMigrationMessage(
                  `${migration.responsesCount} answers from your anonymous session have been saved to your account.`
                );
                trackEvent('Freemium', 'Migration Complete', 'dashboard_auto', migration.responsesCount);
                // Reload progress after migration
                await loadProgress(relId, currentUser.id);
              } else if (!migration.success) {
                console.warn('[Dashboard] Migration had errors:', migration.errors);
                setMigrationMessage(
                  'Some answers from your previous session could not be saved. You may need to re-enter them.'
                );
              }
            } catch (err) {
              console.error('[Dashboard] Migration failed:', err);
            } finally {
              setMigrating(false);
            }
          }

          // Complete any pending version encryption (non-blocking)
          completePendingEncryption(relId, currentUser.id).catch(() => {});
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Refresh progress and onboarding status when component mounts or when returning to dashboard
  useEffect(() => {
    const refreshData = async () => {
      if (user && relationshipId) {
        // Load all data in parallel
        await Promise.all([
          loadProgress(relationshipId, user.id),
          loadJointProgress(relationshipId),
          loadOnboardingStatus(relationshipId, user.id),
          getComfortLevel(relationshipId, user.id).then(level => setComfortLevel(level))
        ]);

        // Reload relationship AFTER progress to detect completion status changes
        const rel = await getRelationship(relationshipId);
        setRelationship(rel);
        console.log('[Dashboard] Relationship data refreshed:', rel);
      }
    };

    refreshData();
  }, [user, relationshipId]);

  // Also refresh when page becomes visible (user switches tabs)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && user && relationshipId) {
        // Reload relationship to detect completion status changes
        const rel = await getRelationship(relationshipId);
        setRelationship(rel);

        await loadProgress(relationshipId, user.id);
        await loadJointProgress(relationshipId);
        await loadOnboardingStatus(relationshipId, user.id);

        const userComfortLevel = await getComfortLevel(relationshipId, user.id);
        setComfortLevel(userComfortLevel);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, relationshipId]);

  // Log when relationship state changes
  useEffect(() => {
    console.log('[Dashboard] Relationship state updated:', relationship);
    if (relationship && user) {
      const isPartner1 = relationship.partner1Id === user.id;
      console.log('[Dashboard] Current user is Partner', isPartner1 ? '1' : '2');
      console.log('[Dashboard] Completion flags:', {
        partner1IndividualComplete: relationship.partner1IndividualComplete,
        partner2IndividualComplete: relationship.partner2IndividualComplete,
        partner1ComparisonAcknowledged: relationship.partner1ComparisonAcknowledged,
        partner2ComparisonAcknowledged: relationship.partner2ComparisonAcknowledged
      });
    }
  }, [relationship, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Helper function to calculate time estimate based on question count
  // Assumes 1.5 minutes per question on average
  const calculateTimeEstimate = (questionCount: number): string => {
    if (questionCount === 0) return "~30-45 min";

    const avgMinutesPerQuestion = 1.5;
    const totalMinutes = questionCount * avgMinutesPerQuestion;

    // Calculate range (±20%)
    const minTimeRaw = totalMinutes * 0.8;
    const maxTimeRaw = totalMinutes * 1.2;

    // Round UP to nearest multiple of 5
    const roundToNearestFive = (num: number) => Math.ceil(num / 5) * 5;
    const minTime = roundToNearestFive(minTimeRaw);
    const maxTime = roundToNearestFive(maxTimeRaw);

    // Format as hours if >= 60 minutes
    if (maxTime >= 60) {
      const minHours = minTime / 60;
      const maxHours = maxTime / 60;

      // If both are over an hour, show as hours
      if (minTime >= 60) {
        return `${minHours.toFixed(1)}-${maxHours.toFixed(1)} hrs`;
      } else {
        // Mixed: show min in minutes, max in hours
        return `${minTime}-${maxTime} min`;
      }
    }

    return `${minTime}-${maxTime} min`;
  };

  // Dynamic stage status calculation
  const getStageStatus = (stageId: number) => {
    console.log(`[Dashboard] getStageStatus(${stageId}) called`);
    console.log(`  - isAdmin: ${isAdmin}`);
    console.log(`  - user?.id: ${user?.id}`);
    console.log(`  - relationship:`, relationship);

    // Admin users have all stages unlocked, but still check for completion
    if (isAdmin) {
      console.log(`  ℹAdmin user - stages unlocked but checking completion status`);
      // Don't return early - continue to normal status check logic
      // This allows admins to see proper completion status while having all stages unlocked
    }

    switch (stageId) {
      case 1: // Onboarding
        // Always unlocked - show status based on comfort level selection
        const onboardingStatus = onboardingComplete
          ? "completed"
          : (comfortLevel ? "in-progress" : "not-started");
        return {
          status: onboardingStatus,
          locked: false
        };

      case 2: // Individual Work
        // Always unlocked - users can start individual work independently of partner
        // Check if marked as complete in relationship document
        const isPartner1 = relationship?.partner1Id === user?.id;
        const individualComplete = isPartner1
          ? relationship?.partner1IndividualComplete
          : relationship?.partner2IndividualComplete;

        console.log('[Dashboard] Individual Work status check:');
        console.log(`  - Comfort Level: ${comfortLevel}`);
        console.log(`  - Progress: ${progress.answered}/${progress.total} (${progress.percentage}%)`);
        console.log(`  - Is Partner 1: ${isPartner1}`);
        console.log(`  - Individual Complete Flag: ${individualComplete}`);
        console.log(`  - Partner 1 Complete: ${relationship?.partner1IndividualComplete}`);
        console.log(`  - Partner 2 Complete: ${relationship?.partner2IndividualComplete}`);

        if (individualComplete) {
          console.log('  Status: COMPLETED (via relationship flag)');
          return { status: "completed", locked: false };
        }
        // Also check progress counts as fallback
        else if (progress.percentage === 100 || (progress.total > 0 && progress.answered === progress.total)) {
          console.log('  Status: COMPLETED (via progress count)');
          return { status: "completed", locked: false };
        } else if (progress.percentage > 0 || progress.answered > 0) {
          console.log('  Status: IN PROGRESS');
          return { status: "in-progress", locked: false };
        } else {
          console.log('  Status: NOT STARTED');
          return { status: "not-started", locked: false };
        }

      case 3: // Comparison View
        // Requires both partners to complete individual work (unless admin)
        if (isAdmin || (relationship?.status === 'active' &&
          relationship?.partner1IndividualComplete &&
          relationship?.partner2IndividualComplete)) {
          // Check if acknowledged
          const isPartner1 = relationship?.partner1Id === user?.id;
          const myAcknowledgement = isPartner1
            ? relationship?.partner1ComparisonAcknowledged
            : relationship?.partner2ComparisonAcknowledged;

          if (myAcknowledgement) {
            return { status: "completed", locked: false };
          }
          return { status: "not-started", locked: false };
        }
        return { status: "locked", locked: true };

      case 4: // Joint Collaboration
        // Requires both partners to acknowledge comparison (unless admin)
        if (isAdmin || (relationship?.partner1ComparisonAcknowledged &&
          relationship?.partner2ComparisonAcknowledged)) {
          // Check completion status
          if (jointProgress.percentage === 100) {
            return { status: "completed", locked: false };
          }
          // Check if any joint responses exist
          if (jointProgress.answered > 0) {
            return { status: "in-progress", locked: false };
          }
          return { status: "not-started", locked: false };
        }
        return { status: "locked", locked: true };

      case 5: // Contract Review
        // Unlocks when joint collaboration is 100% complete
        if (isAdmin || jointProgress.percentage === 100) {
          // Check if contract has been reviewed (could track this in relationship doc)
          return { status: "not-started", locked: false };
        }
        return { status: "locked", locked: true };

      case 6: // Signing
        // Admins have all stages unlocked
        if (isAdmin) {
          return { status: "not-started", locked: false };
        }
        // Unlock when joint collaboration is complete AND relationship is at review or later stage
        if (jointProgress.percentage === 100) {
          const stage = relationship?.currentStage;
          if (stage === 'review' || stage === 'signing' || stage === 'completed') {
            return {
              status: stage === 'completed' ? 'completed' : 'not-started',
              locked: false
            };
          }
        }
        return { status: "locked", locked: true };

      default:
        return { status: "locked", locked: true };
    }
  };

  // 6-part process workflow stages
  const stage1Status = getStageStatus(1);
  const stage2Status = getStageStatus(2);
  const stage3Status = getStageStatus(3);
  const stage4Status = getStageStatus(4);
  const stage5Status = getStageStatus(5);
  const stage6Status = getStageStatus(6);

  const workflowStages = [
    {
      id: 1,
      title: "Onboarding",
      description: "Learn about the process and get started",
      icon: FileText,
      route: "/onboarding",
      status: stage1Status.status,
      locked: stage1Status.locked,
      timeEstimate: "5 min",
      details: "Introduction to the 6-step process"
    },
    {
      id: 2,
      title: "Individual Work",
      description: "Complete your personal questionnaire",
      icon: User,
      route: "/individual-work",
      status: stage2Status.status,
      locked: stage2Status.locked,
      progress: progress.percentage, // Show actual questionnaire progress
      questionCount: progress.total,
      answeredCount: progress.answered,
      timeEstimate: calculateTimeEstimate(progress.total),
      details: `${progress.answered}/${progress.total} questions answered`
    },
    {
      id: 3,
      title: "Comparison View",
      description: "Review and discuss differences with partner",
      icon: GitCompare,
      route: "/comparison",
      status: stage3Status.status,
      locked: stage3Status.locked,
      timeEstimate: "20-30 min"
    },
    {
      id: 4,
      title: "Joint Collaboration",
      description: "Work together to resolve conflicts",
      icon: MessagesSquare,
      route: "/joint-collaboration",
      status: stage4Status.status,
      locked: stage4Status.locked,
      questionCount: jointProgress.total,
      answeredCount: jointProgress.answered,
      progress: jointProgress.percentage,
      timeEstimate: calculateTimeEstimate(jointProgress.total),
      details: `${jointProgress.answered}/${jointProgress.total} questions answered together`
    },
    {
      id: 5,
      title: "Contract Review",
      description: "Review your personalized agreement",
      icon: FileCheck,
      route: "/contract-review",
      status: stage5Status.status,
      locked: stage5Status.locked,
      timeEstimate: "15-20 min"
    },
    {
      id: 6,
      title: "Signing",
      description: "Sign and finalize the agreement",
      icon: PenTool,
      route: "/contract-signing",
      status: stage6Status.status,
      locked: stage6Status.locked,
      timeEstimate: "5 min"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-success/20 text-success border-success/30";
      case "in-progress":
        return "bg-not-started/20 text-muted-foreground border-not-started/30"; // Warm taupe (same as not-started)
      case "not-started":
        return "bg-not-started/20 text-muted-foreground border-not-started/30"; // Warm taupe
      case "locked":
        return "bg-accent/20 text-accent border-accent/30"; // Terra cotta
      default:
        return "bg-not-started/20 text-muted-foreground border-not-started/30"; // Warm taupe default
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-5 w-5" />;
      case "in-progress":
        return <Clock className="h-5 w-5" />;
      case "not-started":
        return <ArrowRight className="h-5 w-5" />;
      case "locked":
        return <Lock className="h-5 w-5" />;
      default:
        return <Clock className="h-5 w-5" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "in-progress":
        return "In Progress";
      case "not-started":
        return "Ready to Start";
      case "locked":
        return "Locked";
      default:
        return "Not Started";
    }
  };

  // Calculate overall workflow progress with weighted contributions
  // Each stage contributes proportionally based on questions answered
  const stageWeight = 100 / 6; // Each stage is worth ~16.67% of total progress

  let workflowProgress = 0;

  // Stage 1: Onboarding (binary)
  if (stage1Status.status === "completed") {
    workflowProgress += stageWeight;
  }

  // Stage 2: Individual Work (proportional based on questions)
  if (progress.total > 0) {
    const individualProgress = (progress.answered / progress.total) * stageWeight;
    workflowProgress += individualProgress;
  }

  // Stage 3: Comparison View (binary)
  if (stage3Status.status === "completed") {
    workflowProgress += stageWeight;
  }

  // Stage 4: Joint Collaboration (proportional based on questions)
  if (jointProgress.total > 0) {
    const jointProgressContribution = (jointProgress.answered / jointProgress.total) * stageWeight;
    workflowProgress += jointProgressContribution;
  }

  // Stage 5: Contract Review (binary)
  if (stage5Status.status === "completed") {
    workflowProgress += stageWeight;
  }

  // Stage 6: Signing (binary)
  if (stage6Status.status === "completed") {
    workflowProgress += stageWeight;
  }

  workflowProgress = Math.round(workflowProgress);

  // Calculate current stage number (first non-completed stage)
  const currentStageNumber = workflowStages.findIndex(s => s.status !== "completed") + 1 || workflowStages.length;
  const totalStages = workflowStages.length;

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background">
      <div className="layout-container flex h-full grow flex-col">
        {/* Header */}
        <AppHeader />

        {/* Main Content */}
        <main id="main-content" className="flex-1 p-4 sm:p-6 lg:p-10">
          <div className="max-w-7xl mx-auto space-y-8">
            {/* FREEMIUM: Migration status banner */}
            {migrating && (
              <Alert className="border-primary/30 bg-primary/5">
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Saving your anonymous answers to your account...
                </AlertDescription>
              </Alert>
            )}
            {migrationMessage && !migrating && (
              <Alert className="border-success/30 bg-success/5">
                <CheckCircle className="h-4 w-4 text-success" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{migrationMessage}</span>
                  <button
                    onClick={() => setMigrationMessage(null)}
                    className="text-muted-foreground hover:text-foreground ml-4 text-sm underline"
                  >
                    Dismiss
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {/* Page Heading */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-foreground text-4xl font-black leading-tight tracking-[-0.033em]">
                    Contract Dashboard
                  </h1>
                  <p className="text-muted-foreground text-base font-normal leading-normal">
                    Track your progress through the contract creation process
                  </p>
                </div>
              </div>
            </div>

            {/* Overall Progress and Partner Pairing - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Overall Workflow Progress */}
              <div className="flex flex-col gap-4 p-6 bg-card rounded-xl border border-border shadow-sm">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-between items-start sm:items-center">
                  <div>
                    <h3 className="text-foreground text-2xl font-bold leading-normal">Overall Progress</h3>
                    <p className="text-muted-foreground text-sm">
                      Stage {currentStageNumber} of {totalStages}
                    </p>
                  </div>
                  <p className="text-foreground text-2xl font-black">{workflowProgress}%</p>
                </div>
                <Progress value={workflowProgress} className="h-3" />
              </div>

              {/* Partner Pairing */}
              <PartnerPairing
                userId={user.id}
                userEmail={user.email}
                relationshipId={relationshipId}
                onPaired={(newRelationshipId) => {
                  setRelationshipId(newRelationshipId);
                  // Reload relationship data
                  getRelationship(newRelationshipId).then(rel => {
                    setRelationship(rel);
                  });
                }}
              />
            </div>

            {/* Store */}
            <button
              onClick={() => navigate("/store")}
              className="w-full text-left p-6 bg-card rounded-xl border-2 border-border hover:border-primary/50 hover:bg-muted hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="flex-shrink-0 size-12 flex items-center justify-center">
                <ShoppingBag className="h-10 w-10 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground text-2xl font-bold leading-tight">Store</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Explore and unlock additional question decks to enrich your Love Contract.
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            </button>

            {/* Workflow Stages */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">Process Stages</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                {workflowStages.map((stage, index) => {
                  const Icon = stage.icon;
                  const isClickable = !stage.locked;

                  const StageCard = (
                    <div
                      className={`flex flex-col gap-4 p-6 bg-card rounded-xl border-2 transition-all h-full ${getStatusColor(stage.locked ? "locked" : stage.status)
                        } ${isClickable
                          ? "hover:shadow-lg cursor-pointer"
                          : "cursor-not-allowed"
                        }`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-4 flex-grow">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="flex-shrink-0 size-12 flex items-center justify-center">
                            <Icon className="h-6 w-6" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-bold text-muted-foreground">
                                STAGE {index + 1}
                              </span>
                              {stage.timeEstimate && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {stage.timeEstimate}
                                </span>
                              )}
                            </div>
                            <h3 className="text-foreground text-2xl font-bold leading-tight">
                              {stage.title}
                            </h3>
                            <p className="text-muted-foreground text-sm mt-1">
                              {stage.description}
                            </p>
                            {stage.details && (
                              <p className="text-muted-foreground text-xs mt-1.5 font-medium">
                                {stage.details}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusIcon(stage.locked ? "locked" : stage.status)}
                          <span className={`text-xs font-medium ${stage.status === "completed"
                            ? "text-green-600"
                            : stage.status === "in-progress"
                              ? "text-primary"
                              : stage.locked
                                ? "text-muted-foreground"
                                : "text-muted-foreground"
                            }`}>
                            {getStatusText(stage.locked ? "locked" : stage.status)}
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar (only for in-progress stages with progress data) */}
                      {stage.status === "in-progress" && stage.progress !== undefined && (
                        <div className="space-y-2" data-testid={stage.id === 2 ? "individual-progress" : undefined}>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Questionnaire Progress</span>
                            <span className="font-medium text-foreground">{stage.progress}%</span>
                          </div>
                          <Progress value={stage.progress} className="h-2" />
                        </div>
                      )}
                    </div>
                  );

                  return isClickable ? (
                    <Link key={stage.id} to={stage.route} onClick={() => trackEvent('Dashboard', 'Stage Navigate', stage.title)}>
                      {StageCard}
                    </Link>
                  ) : (
                    <div key={stage.id}>{StageCard}</div>
                  );
                })}
              </div>
            </div>

            {/* Relationship Insights Section */}
            <div className="space-y-4 mt-12">
              <h2 className="text-2xl font-bold text-foreground">Additional Resources</h2>
              <Link
                to="/relationship-insights"
                className={`flex flex-col gap-4 p-6 bg-card rounded-xl border-2 transition-all ${jointProgress.answered > 0 || isAdmin
                  ? "border-primary/20 hover:shadow-lg cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10"
                  : "opacity-60 cursor-not-allowed border-border"
                  }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-shrink-0 size-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-foreground text-2xl font-bold leading-tight">
                        Relationship Insights
                      </h3>
                      <p className="text-muted-foreground text-sm mt-1">
                        View your personalized analysis and recommendations
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {jointProgress.answered > 0 || isAdmin ? (
                      <ArrowRight className="h-5 w-5 text-primary" />
                    ) : (
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </Link>
            </div>

            {/* Contract Version History & Subscription */}
            {relationship && (relationship.contractVersionCount > 0 || relationship.contractFinalized) && (
              <div className="space-y-4 mt-12">
                <h2 className="text-2xl font-bold text-foreground">Your Contract</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/version-history"
                    className="flex flex-col gap-3 p-6 bg-card rounded-xl border-2 border-primary/20 hover:shadow-lg transition-all bg-gradient-to-br from-primary/5 to-primary/10"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="flex-shrink-0 size-12 flex items-center justify-center bg-primary/10 rounded-lg">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-foreground text-lg font-bold leading-tight">
                            Version History
                          </h3>
                          <p className="text-muted-foreground text-sm mt-1">
                            {relationship.contractVersionCount
                              ? `${relationship.contractVersionCount} version${relationship.contractVersionCount !== 1 ? 's' : ''} saved`
                              : 'View your contract history'}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {!isPremium && relationship.contractVersionCount > 1 ? (
                          <Lock className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ArrowRight className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  </Link>
                  <SubscriptionCard userProfile={premiumProfile} isPremium={isPremium} />
                </div>
              </div>
            )}

            {/* Help Section */}
            <Link to="/help" className="block">
              <div className="bg-primary/10 p-6 rounded-xl border border-primary/20 mt-8 hover:bg-primary/15 transition-colors cursor-pointer">
                <div className="flex items-start gap-4">
                  <HelpCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-foreground font-bold mb-1">Need Help?</p>
                      <ArrowRight className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Visit our Help Center for guides, FAQs, and support resources.
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </main>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        productId="full-access"
        amount={PRICING.oneTime.amount}
        productName="Premium Relationship Contract"
        productDescription="Unlock full access to all features"
        onSuccess={() => {
          console.log('Payment successful!');
          // You can add logic here to update user profile with hasPurchased flag
        }}
      />

      <Footer />
    </div>
  );
};

export default Dashboard;
